import React from "react";
import { Head, Link, router, usePage } from "@inertiajs/react";
import styled from "styled-components";
import SectionErrorBoundary from "../../homes/SectionErrorBoundary";
import PortalApp from "../../portal/App";
import {
  PortalContainer,
  PortalSection,
  PortalCard,
  PortalEmpty,
} from "../../portal/PortalCard";
import {
  brand_orange_deep,
  brand_gradient,
  card_shadow_md,
  narrowScreenMediaQuery,
} from "../../constants/styles";
import {
  openBlankPopup,
  openXendit,
  payViaPopup,
  postJson,
} from "../../portal/payments";
import { getCsrfToken } from "../../util";

export default function Membership() {
  const {
    active_packages: activePackages = [],
    pending_payments: pendingPayments = [],
    payment_history: paymentHistory = [],
    plans = { popular: [], others: [] },
    profile = {},
    pay_path: payPath = "/memberships_v2/pay_invoice",
    quote_path: quotePath = "/memberships_v2/quote",
    validate_referral_path:
      validateReferralPath = "/api/v1/users/validate_referral_code",
  } = usePage().props;

  const [historyFilter, setHistoryFilter] = React.useState("all");
  const [showPlans, setShowPlans] = React.useState(false);
  const [selectedPlan, setSelectedPlan] = React.useState(null);
  const planSectionRef = React.useRef(null);

  const allPlans = React.useMemo(
    () => [...(plans.popular || []), ...(plans.others || [])],
    [plans],
  );

  const filteredHistory = React.useMemo(
    () =>
      paymentHistory.filter(
        (p) => historyFilter === "all" || p.status === historyFilter,
      ),
    [paymentHistory, historyFilter],
  );

  // A still-payable pending invoice blocks a second checkout (the modal). An
  // expired one does not — the user can freely start a fresh payment.
  const activePending = React.useMemo(
    () => pendingPayments.find((p) => !p.is_expired && p.xendit_url),
    [pendingPayments],
  );

  const openPlans = () => {
    setSelectedPlan(null);
    setShowPlans(true);
  };

  const closePlans = () => {
    setSelectedPlan(null);
    setShowPlans(false);
  };

  React.useEffect(() => {
    if (showPlans && planSectionRef.current) {
      planSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [showPlans, selectedPlan]);

  return (
    <>
      <Head>
        <title>Membership — Taxsam Learning Center</title>
        <meta
          name="description"
          content="Kelola paket berlangganan, pembayaran tertunda, dan riwayat pembayaran."
        />
      </Head>
      <PortalContainer>
        <SectionErrorBoundary name="ActivePackages">
          <PortalSection
            title="Paket Aktif"
            subtitle={
              activePackages.length > 0
                ? `${activePackages.length} paket berlangganan sedang aktif.`
                : "Belum ada paket berlangganan aktif."
            }
            action={
              activePackages.length > 0 && !showPlans ? (
                <GhostButton type="button" onClick={openPlans}>
                  + Tambah Paket
                </GhostButton>
              ) : null
            }
          >
            {activePackages.length > 0 ? (
              <CardGrid>
                {activePackages.map((p) => (
                  <ActivePackageCard key={p.id}>
                    <PackageEyebrow>
                      {p.group ? p.group.toUpperCase() : "PAKET"}
                    </PackageEyebrow>
                    <PackageName>{p.name}</PackageName>
                    <PackageDates>
                      {p.started_at && formatDateID(p.started_at)}{" "}
                      {p.ended_at && `→ ${formatDateID(p.ended_at)}`}
                    </PackageDates>
                    {p.remaining_days != null && (
                      <RemainingPill $low={p.remaining_days <= 30}>
                        {p.remaining_days > 0
                          ? `${p.remaining_days} hari tersisa`
                          : "Berakhir hari ini"}
                      </RemainingPill>
                    )}
                    {p.can_claim_certificate && (
                      <CertCta href="/sertifikat">
                        Klaim Sertifikat Bundle →
                      </CertCta>
                    )}
                  </ActivePackageCard>
                ))}
              </CardGrid>
            ) : (
              <PortalEmpty>
                <p style={{ marginBottom: 14 }}>
                  Aktifkan paket berlangganan untuk mengakses seluruh materi.
                </p>
                {!showPlans && (
                  <PrimaryAction as="button" type="button" onClick={openPlans}>
                    Mulai Berlangganan
                  </PrimaryAction>
                )}
              </PortalEmpty>
            )}
          </PortalSection>
        </SectionErrorBoundary>

        {showPlans && (
          <SectionErrorBoundary name="PlanSelection">
            <div ref={planSectionRef}>
              <PortalSection
                title={
                  selectedPlan
                    ? "Konfirmasi & Bayar"
                    : "Pilih Paket Berlangganan"
                }
                subtitle={
                  selectedPlan
                    ? "Periksa detail paket, lalu lanjutkan ke pembayaran."
                    : "Pilih paket yang paling sesuai dengan kebutuhan Anda."
                }
                action={
                  <GhostButton type="button" onClick={closePlans}>
                    Tutup
                  </GhostButton>
                }
              >
                {selectedPlan ? (
                  <CheckoutPanel
                    key={selectedPlan.id}
                    plan={selectedPlan}
                    profile={profile}
                    payPath={payPath}
                    quotePath={quotePath}
                    validateReferralPath={validateReferralPath}
                    activePending={activePending}
                    onBack={() => setSelectedPlan(null)}
                  />
                ) : allPlans.length > 0 ? (
                  <CardGrid>
                    {allPlans.map((plan) => (
                      <PlanCard
                        key={plan.id}
                        plan={plan}
                        onSelect={setSelectedPlan}
                      />
                    ))}
                  </CardGrid>
                ) : (
                  <PortalEmpty>Paket berlangganan belum tersedia.</PortalEmpty>
                )}
              </PortalSection>
            </div>
          </SectionErrorBoundary>
        )}

        {pendingPayments.length > 0 && (
          <SectionErrorBoundary name="PendingPayments">
            <PortalSection
              title="Menunggu Pembayaran"
              subtitle="Selesaikan pembayaran untuk mengaktifkan paket berlangganan."
            >
              <PendingList>
                {pendingPayments.map((p) => (
                  <PendingCard key={p.id}>
                    <PendingBody>
                      <PendingName>{p.name}</PendingName>
                      {p.price_formatted && (
                        <PendingPrice>{p.price_formatted}</PendingPrice>
                      )}
                      {p.description && (
                        <PendingDesc>{p.description}</PendingDesc>
                      )}
                      <PendingMeta>
                        {p.created_at && (
                          <span>Dibuat {formatDateTimeID(p.created_at)}</span>
                        )}
                        {p.expired_at && (
                          <span>
                            Berlaku sampai {formatDateTimeID(p.expired_at)}
                          </span>
                        )}
                      </PendingMeta>
                    </PendingBody>
                    {p.is_expired ? (
                      <ExpiredTag>Kedaluwarsa</ExpiredTag>
                    ) : (
                      p.xendit_url && (
                        <PrimaryAction
                          as="button"
                          type="button"
                          onClick={() => openXendit(p.xendit_url)}
                        >
                          Lanjut Bayar →
                        </PrimaryAction>
                      )
                    )}
                  </PendingCard>
                ))}
              </PendingList>
            </PortalSection>
          </SectionErrorBoundary>
        )}

        <SectionErrorBoundary name="PaymentHistory">
          <PortalSection
            title="Riwayat Pembayaran"
            subtitle="Lihat semua pembayaran Anda — paid, pending, atau gagal."
            action={
              <SegmentBar>
                {[
                  { k: "all", label: "Semua" },
                  { k: "paid", label: "Lunas" },
                  { k: "pending", label: "Pending" },
                  { k: "failed", label: "Gagal" },
                ].map((f) => (
                  <SegmentBtn
                    key={f.k}
                    $active={historyFilter === f.k}
                    onClick={() => setHistoryFilter(f.k)}
                  >
                    {f.label}
                  </SegmentBtn>
                ))}
              </SegmentBar>
            }
          >
            {filteredHistory.length > 0 ? (
              <HistoryTable>
                <thead>
                  <tr>
                    <th>Paket</th>
                    <th>Tanggal</th>
                    <th>Harga</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHistory.map((row) => (
                    <tr key={row.id}>
                      <td>{row.name}</td>
                      <td>
                        {row.created_at ? formatDateID(row.created_at) : "—"}
                      </td>
                      <td>
                        {row.price != null ? formatRupiah(row.price) : "—"}
                      </td>
                      <td>
                        <StatusPill $status={row.status}>
                          {STATUS_LABELS[row.status] || row.status || "—"}
                        </StatusPill>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </HistoryTable>
            ) : (
              <PortalEmpty>Belum ada pembayaran tercatat.</PortalEmpty>
            )}
          </PortalSection>
        </SectionErrorBoundary>
      </PortalContainer>
    </>
  );
}

Membership.layout = (page) => <PortalApp>{page}</PortalApp>;

const STATUS_LABELS = {
  paid: "Lunas",
  pending: "Pending",
  failed: "Gagal",
};

function formatDateID(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch (_) {
    return iso;
  }
}

function formatDateTimeID(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("id-ID", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_) {
    return iso;
  }
}

function formatRupiah(n) {
  try {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      maximumFractionDigits: 0,
    }).format(n);
  } catch (_) {
    return `Rp ${n}`;
  }
}

const PlanCard = (props) => {
  const plan = props.plan;
  return (
    <PlanCardBox $popular={plan.popular}>
      {plan.popular && <PlanBadge>Populer</PlanBadge>}
      <PackageName>{plan.name}</PackageName>
      {plan.description && <PlanDesc>{plan.description}</PlanDesc>}
      <PlanPrice>{plan.price_formatted}</PlanPrice>
      <PlanMetaList>
        <li>Durasi {plan.duration} hari</li>
        <li>
          {plan.certificates > 0
            ? `${plan.certificates} sertifikat dapat diklaim`
            : "Tanpa klaim sertifikat"}
        </li>
      </PlanMetaList>
      {plan.certificate_names?.length > 0 && (
        <CertList>
          {plan.certificate_names.map((name) => (
            <li key={name}>{name}</li>
          ))}
        </CertList>
      )}
      <SelectButton type="button" onClick={() => props.onSelect(plan)}>
        Pilih Paket
      </SelectButton>
    </PlanCardBox>
  );
};

const CheckoutPanel = (props) => {
  const plan = props.plan;
  const profile = props.profile || {};

  const [couponInput, setCouponInput] = React.useState("");
  const [coupon, setCoupon] = React.useState({ status: "idle" });
  const [referralInput, setReferralInput] = React.useState("");
  const [referral, setReferral] = React.useState({ status: "idle" });

  const couponApplied = coupon.status === "valid";
  const displayTotal = couponApplied ? coupon.total : plan.price_formatted;
  // A typed referral must validate as valid before submit is allowed —
  // "valid can go, invalid no go". Empty referral is fine (optional).
  const referralPending =
    referralInput.trim().length > 0 && referral.status !== "valid";
  const submitDisabled =
    coupon.status === "checking" ||
    referral.status === "checking" ||
    referralPending;

  const jsonHeaders = () => ({
    "Content-Type": "application/json",
    Accept: "application/json",
    "X-CSRF-Token": getCsrfToken(),
  });

  const applyCoupon = async () => {
    const code = couponInput.trim();
    if (!code) {
      setCoupon({ status: "idle" });
      return;
    }
    setCoupon({ status: "checking" });
    try {
      const res = await fetch(props.quotePath, {
        method: "POST",
        headers: jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({ subs_payment_setting: plan.id, code }),
      });
      const data = await res.json();
      if (res.ok && data.valid) {
        setCoupon({
          status: "valid",
          couponId: data.coupon_id,
          discount: data.discount_formatted,
          total: data.total_formatted,
        });
      } else {
        setCoupon({
          status: "invalid",
          message: data.message || "Kupon tidak ditemukan.",
        });
      }
    } catch (_) {
      setCoupon({ status: "invalid", message: "Gagal memvalidasi kupon." });
    }
  };

  const clearCoupon = () => {
    setCouponInput("");
    setCoupon({ status: "idle" });
  };

  const validateReferral = async () => {
    const code = referralInput.trim();
    if (!code) {
      setReferral({ status: "idle" });
      return;
    }
    if (code.length !== 6) {
      setReferral({
        status: "invalid",
        message: "Format kode referral harus 6 karakter.",
      });
      return;
    }
    setReferral({ status: "checking" });
    try {
      const res = await fetch(
        `${props.validateReferralPath}?referral_code=${encodeURIComponent(code)}`,
        { method: "POST", headers: jsonHeaders(), credentials: "same-origin" },
      );
      setReferral(
        res.ok
          ? { status: "valid" }
          : { status: "invalid", message: "Kode referral tidak valid." },
      );
    } catch (_) {
      setReferral({
        status: "invalid",
        message: "Gagal memvalidasi kode referral.",
      });
    }
  };

  const [fullName, setFullName] = React.useState(profile.name || "");
  const [phone, setPhone] = React.useState(profile.phone || "");
  const [paying, setPaying] = React.useState(false);
  const [payError, setPayError] = React.useState("");
  const [showPending, setShowPending] = React.useState(false);

  const payBody = () => ({
    subs_payment_setting: plan.id,
    coupon_id: couponApplied ? coupon.couponId : "",
    referral_code: referralInput.trim(),
    full_name: fullName,
    phone_number: phone,
  });

  const reloadPortal = () =>
    router.reload({
      only: [
        "pending_payments",
        "payment_history",
        "active_packages",
        "profile",
      ],
    });

  const finishPay = (res, syncAnyway) => {
    setPaying(false);
    if (res.paid) {
      router.reload();
      return;
    }
    if (!res.ok) {
      setPayError(res.error || "Gagal memproses pembayaran.");
      // A prior cancel already mutated server state — resync the page even
      // when the follow-up payment failed, so stale rows don't linger.
      if (syncAnyway) reloadPortal();
      return;
    }
    // Popup is on its way to Xendit — refresh so the new pending invoice shows.
    reloadPortal();
  };

  const doPay = async (popup) => {
    setPayError("");
    setPaying(true);
    const res = await payViaPopup(props.payPath, payBody(), popup);
    finishPay(res);
  };

  const onPayClick = () => {
    setPayError("");
    if (props.activePending) {
      setShowPending(true);
      return;
    }
    doPay();
  };

  // Cancel-then-pay: the popup must be opened in this click (before the async
  // cancel) or the browser blocks it, so we open it up front and hand it down.
  const cancelThenPay = async () => {
    setShowPending(false);
    const popup = openBlankPopup();
    setPaying(true);
    const cancelled = await postJson(props.activePending.cancel_url, {});
    if (!cancelled.ok) {
      if (popup) popup.close();
      setPaying(false);
      setPayError(cancelled.error || "Gagal membatalkan pembayaran tertunda.");
      return;
    }
    // Cancel succeeded; resync even if the new payment then fails.
    const res = await payViaPopup(props.payPath, payBody(), popup);
    finishPay(res, true);
  };

  const payDisabled = submitDisabled || paying;

  return (
    <CheckoutCard>
      {showPending && props.activePending && (
        <PendingPaymentModal
          pending={props.activePending}
          onClose={() => setShowPending(false)}
          onPayPrevious={() => {
            setShowPending(false);
            openXendit(props.activePending.xendit_url);
          }}
          onCancelAndPay={cancelThenPay}
        />
      )}
      <CheckoutHead>
        <div>
          <PackageEyebrow>Paket Dipilih</PackageEyebrow>
          <PackageName>{plan.name}</PackageName>
          {plan.description && <PlanDesc>{plan.description}</PlanDesc>}
        </div>
        <div style={{ textAlign: "right" }}>
          {couponApplied && <StrikePrice>{plan.price_formatted}</StrikePrice>}
          <CheckoutPrice>{displayTotal}</CheckoutPrice>
        </div>
      </CheckoutHead>

      <CheckoutMeta>
        <span>Durasi {plan.duration} hari</span>
        {plan.certificates > 0 && (
          <span>{plan.certificates} sertifikat dapat diklaim</span>
        )}
      </CheckoutMeta>

      {plan.certificate_names?.length > 0 && (
        <CertList>
          {plan.certificate_names.map((name) => (
            <li key={name}>{name}</li>
          ))}
        </CertList>
      )}

      <Field as="div">
        <span>Kode Kupon (opsional)</span>
        <InlineRow>
          <input
            value={couponInput}
            disabled={couponApplied || coupon.status === "checking"}
            onChange={(e) => setCouponInput(e.target.value)}
            placeholder="Masukkan kode kupon"
            style={{ textTransform: "uppercase" }}
          />
          {couponApplied ? (
            <GhostButton type="button" onClick={clearCoupon}>
              Hapus
            </GhostButton>
          ) : (
            <GhostButton
              type="button"
              onClick={applyCoupon}
              disabled={coupon.status === "checking" || !couponInput.trim()}
            >
              {coupon.status === "checking" ? "Memeriksa..." : "Terapkan"}
            </GhostButton>
          )}
        </InlineRow>
      </Field>
      {couponApplied && (
        <HintOk>Kupon dipakai — potongan {coupon.discount}.</HintOk>
      )}
      {coupon.status === "invalid" && <HintErr>{coupon.message}</HintErr>}

      <div>
        {!profile.complete && (
          <Fieldset>
            <FieldLabel>
              Lengkapi data berikut untuk melanjutkan pembayaran.
            </FieldLabel>
            <Field>
              <span>Nama Lengkap</span>
              <input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Nama lengkap"
              />
            </Field>
            <Field>
              <span>Nomor Handphone</span>
              <input
                inputMode="numeric"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="08xxxxxxxxxx"
              />
            </Field>
          </Fieldset>
        )}

        <Field as="div">
          <span>Kode Referral (opsional)</span>
          <input
            value={referralInput}
            maxLength={6}
            placeholder="6 karakter"
            style={{ textTransform: "uppercase" }}
            onChange={(e) => {
              setReferralInput(e.target.value);
              setReferral({ status: "idle" });
            }}
            onBlur={validateReferral}
          />
        </Field>
        {referral.status === "checking" && (
          <HintMuted>Memeriksa kode...</HintMuted>
        )}
        {referral.status === "valid" && <HintOk>Kode referral valid.</HintOk>}
        {referral.status === "invalid" && <HintErr>{referral.message}</HintErr>}

        {payError && <HintErr>{payError}</HintErr>}

        <CheckoutActions>
          <GhostButton type="button" onClick={props.onBack}>
            Kembali
          </GhostButton>
          <PrimaryAction
            as="button"
            type="button"
            disabled={payDisabled}
            onClick={onPayClick}
          >
            {paying ? "Memproses..." : "Bayar Sekarang →"}
          </PrimaryAction>
        </CheckoutActions>
      </div>
    </CheckoutCard>
  );
};

const PendingPaymentModal = (props) => {
  const pending = props.pending;
  return (
    <PendingBackdrop role="dialog" aria-modal="true" onClick={props.onClose}>
      <PendingModalCard onClick={(e) => e.stopPropagation()}>
        <PendingModalTitle>Masih ada pembayaran tertunda</PendingModalTitle>
        <PendingModalLede>
          Anda sudah memulai pembayaran untuk paket berlangganan dan belum
          menyelesaikannya. Untuk menjaga akun tetap rapi, selesaikan dulu
          pembayaran yang ada — atau batalkan agar bisa membayar paket yang baru
          dipilih.
        </PendingModalLede>
        <PendingDetailBox>
          <PendingDetailRow>
            <span>Paket</span>
            <strong>{pending.name}</strong>
          </PendingDetailRow>
          {pending.price_formatted && (
            <PendingDetailRow>
              <span>Total</span>
              <strong>{pending.price_formatted}</strong>
            </PendingDetailRow>
          )}
          {pending.description && (
            <PendingDetailDesc>{pending.description}</PendingDetailDesc>
          )}
          {pending.created_at && (
            <PendingDetailRow>
              <span>Dibuat</span>
              <span>{formatDateTimeID(pending.created_at)}</span>
            </PendingDetailRow>
          )}
          {pending.expired_at && (
            <PendingDetailRow>
              <span>Berlaku sampai</span>
              <span>{formatDateTimeID(pending.expired_at)}</span>
            </PendingDetailRow>
          )}
        </PendingDetailBox>
        <PendingModalActions>
          <GhostButton type="button" onClick={props.onClose}>
            Tutup / tidak jadi
          </GhostButton>
          {pending.xendit_url && (
            <SecondaryAction type="button" onClick={props.onPayPrevious}>
              Bayar membership sebelumnya
            </SecondaryAction>
          )}
          <PrimaryAction
            as="button"
            type="button"
            onClick={props.onCancelAndPay}
          >
            Batalkan & bayar paket ini
          </PrimaryAction>
        </PendingModalActions>
      </PendingModalCard>
    </PendingBackdrop>
  );
};

// ---- Styles ---------------------------------------------------------------

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 14px;
`;

const ActivePackageCard = styled(PortalCard)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
  border: 1px solid rgba(216, 89, 7, 0.32);

  [data-theme="dark"] & {
    background: linear-gradient(
      135deg,
      rgba(216, 89, 7, 0.18),
      rgba(247, 179, 12, 0.08)
    );
    border-color: rgba(216, 89, 7, 0.4);
  }
`;

const PackageEyebrow = styled.span`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: ${brand_orange_deep};
`;

const PackageName = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  color: #1f2937;
  line-height: 1.3;

  [data-theme="dark"] & {
    color: #f3f4f6;
  }
`;

const PackageDates = styled.span`
  font-size: 12px;
  color: #6b7280;

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const RemainingPill = styled.span`
  display: inline-block;
  width: fit-content;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-top: 4px;
  background: ${({ $low }) =>
    $low ? "rgba(245, 158, 11, 0.16)" : "rgba(16, 185, 129, 0.16)"};
  color: ${({ $low }) => ($low ? "#b45309" : "#047857")};
`;

const CertCta = styled(Link)`
  margin-top: 8px;
  font-size: 12px;
  font-weight: 800;
  color: ${brand_orange_deep};
  text-decoration: none;
  letter-spacing: 0.04em;
  text-transform: uppercase;

  &:hover {
    text-decoration: underline;
  }
`;

const PrimaryAction = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 10px 22px;
  border: none;
  border-radius: 999px;
  background: ${brand_gradient};
  color: #ffffff;
  font-family: inherit;
  font-weight: 800;
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  text-decoration: none;
  cursor: pointer;
  transition:
    filter 150ms ease,
    transform 100ms ease;

  &:hover {
    filter: brightness(0.97);
    transform: translateY(-1px);
    color: #fff;
  }
  &:focus-visible {
    outline: 2px solid #fdba74;
    outline-offset: 2px;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
    filter: none;
  }
`;

const StrikePrice = styled.div`
  font-size: 13px;
  color: #9ca3af;
  text-decoration: line-through;
`;

const InlineRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: stretch;

  input {
    flex: 1;
    min-width: 0;
  }
`;

const HintOk = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  font-weight: 700;
  color: #047857;
`;

const HintErr = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  font-weight: 700;
  color: #b91c1c;
`;

const HintMuted = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  color: #6b7280;
`;

const PendingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PendingCard = styled(PortalCard)`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 16px;
  align-items: center;
  background: rgba(245, 158, 11, 0.06);
  border-color: rgba(245, 158, 11, 0.32);

  @media ${narrowScreenMediaQuery} {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  [data-theme="dark"] & {
    background: rgba(245, 158, 11, 0.1);
    border-color: rgba(245, 158, 11, 0.38);
  }
`;

const PendingBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const PendingName = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #1f2937;
  [data-theme="dark"] & {
    color: #f3f4f6;
  }
`;

const PendingPrice = styled.span`
  font-size: 13px;
  color: ${brand_orange_deep};
  font-weight: 800;
`;

const PendingDesc = styled.span`
  font-size: 12px;
  color: #4b5563;
  [data-theme="dark"] & {
    color: #cbd5e1;
  }
`;

const PendingMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  font-size: 12px;
  color: #6b7280;
  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const ExpiredTag = styled.span`
  align-self: center;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: rgba(220, 38, 38, 0.12);
  color: #b91c1c;
`;

const SecondaryAction = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 10px 18px;
  border-radius: 999px;
  border: 1px solid rgba(216, 89, 7, 0.45);
  background: transparent;
  color: ${brand_orange_deep};
  font-family: inherit;
  font-weight: 800;
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;

  &:hover {
    background: rgba(216, 89, 7, 0.08);
  }
  &:focus-visible {
    outline: 2px solid #fdba74;
    outline-offset: 2px;
  }
  [data-theme="dark"] & {
    color: #fdba74;
    border-color: rgba(247, 179, 12, 0.4);
  }
`;

const PendingBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 23, 42, 0.55);
`;

const PendingModalCard = styled.div`
  width: 100%;
  max-width: 460px;
  max-height: 90vh;
  overflow-y: auto;
  background: #ffffff;
  border-radius: 18px;
  padding: 24px;
  box-shadow: ${card_shadow_md};

  [data-theme="dark"] & {
    background: #1f2937;
  }
`;

const PendingModalTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 800;
  color: #1f2937;
  [data-theme="dark"] & {
    color: #f3f4f6;
  }
`;

const PendingModalLede = styled.p`
  margin: 0 0 16px;
  font-size: 13px;
  line-height: 1.5;
  color: #4b5563;
  [data-theme="dark"] & {
    color: #cbd5e1;
  }
`;

const PendingDetailBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px;
  border-radius: 12px;
  background: rgba(245, 158, 11, 0.07);
  border: 1px solid rgba(245, 158, 11, 0.28);
  margin-bottom: 18px;
`;

const PendingDetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  color: #4b5563;
  [data-theme="dark"] & {
    color: #cbd5e1;
  }
  strong {
    color: #1f2937;
    [data-theme="dark"] & {
      color: #f3f4f6;
    }
  }
`;

const PendingDetailDesc = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: #6b7280;
  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const PendingModalActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SegmentBar = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.04);
  flex-wrap: wrap;

  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.04);
  }
`;

const SegmentBtn = styled.button`
  padding: 6px 14px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  background: ${({ $active }) => ($active ? brand_gradient : "transparent")};
  color: ${({ $active }) => ($active ? "#fff" : "#4b5563")};
  font-size: 12px;
  font-weight: 700;
  transition: filter 150ms ease;

  &:hover:not([disabled]) {
    color: ${brand_orange_deep};
  }
  ${({ $active }) =>
    $active && `&:hover { color: #fff; filter: brightness(0.97); }`}
  &:focus-visible {
    outline: 2px solid #fdba74;
    outline-offset: 2px;
  }

  [data-theme="dark"] & {
    color: ${({ $active }) => ($active ? "#fff" : "#cbd5e1")};
  }
`;

const HistoryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #ffffff;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: ${card_shadow_md};

  th,
  td {
    padding: 12px 18px;
    text-align: left;
    font-size: 13px;
    border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  }

  th {
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #6b7280;
    background: rgba(15, 23, 42, 0.02);
  }

  td {
    color: #1f2937;
  }

  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.03);
    box-shadow: none;
    th {
      color: #9ca3af;
      background: rgba(255, 255, 255, 0.04);
    }
    td {
      color: #e5e7eb;
      border-bottom-color: rgba(255, 255, 255, 0.06);
    }
  }
`;

const StatusPill = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: ${({ $status }) =>
    $status === "paid"
      ? "rgba(16, 185, 129, 0.14)"
      : $status === "pending"
        ? "rgba(245, 158, 11, 0.16)"
        : $status === "failed"
          ? "rgba(220, 38, 38, 0.14)"
          : "rgba(15, 23, 42, 0.06)"};
  color: ${({ $status }) =>
    $status === "paid"
      ? "#047857"
      : $status === "pending"
        ? "#b45309"
        : $status === "failed"
          ? "#b91c1c"
          : "#4b5563"};
`;

// ---- Plan selection + checkout -------------------------------------------

const GhostButton = styled.button`
  padding: 7px 16px;
  border-radius: 999px;
  border: 1px solid rgba(216, 89, 7, 0.4);
  background: transparent;
  color: ${brand_orange_deep};
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 150ms ease;

  &:hover {
    background: rgba(216, 89, 7, 0.08);
  }
  &:focus-visible {
    outline: 2px solid #fdba74;
    outline-offset: 2px;
  }

  [data-theme="dark"] & {
    color: #fdba74;
    border-color: rgba(247, 179, 12, 0.4);
  }
`;

const PlanCardBox = styled(PortalCard)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  border: 1px solid
    ${({ $popular }) =>
      $popular ? "rgba(216, 89, 7, 0.5)" : "rgba(15, 23, 42, 0.1)"};
  box-shadow: ${({ $popular }) => ($popular ? card_shadow_md : "none")};

  [data-theme="dark"] & {
    border-color: ${({ $popular }) =>
      $popular ? "rgba(247, 179, 12, 0.45)" : "rgba(255, 255, 255, 0.1)"};
  }
`;

const PlanBadge = styled.span`
  position: absolute;
  top: -10px;
  right: 16px;
  padding: 4px 12px;
  border-radius: 999px;
  background: ${brand_gradient};
  color: #fff;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const PlanDesc = styled.p`
  margin: 0;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.4;

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const PlanPrice = styled.span`
  font-size: 24px;
  font-weight: 800;
  color: ${brand_orange_deep};
  margin-top: 4px;

  [data-theme="dark"] & {
    color: #fdba74;
  }
`;

const PlanMetaList = styled.ul`
  margin: 4px 0 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  color: #374151;

  [data-theme="dark"] & {
    color: #d1d5db;
  }
`;

const CertList = styled.ul`
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 12px;
  color: #6b7280;

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const SelectButton = styled.button`
  margin-top: auto;
  padding: 10px 18px;
  border-radius: 999px;
  border: none;
  background: ${brand_gradient};
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
  transition:
    filter 150ms ease,
    transform 100ms ease;

  &:hover {
    filter: brightness(0.97);
    transform: translateY(-1px);
  }
  &:focus-visible {
    outline: 2px solid #fdba74;
    outline-offset: 2px;
  }
`;

const CheckoutCard = styled(PortalCard)`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const CheckoutHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
`;

const CheckoutPrice = styled.span`
  font-size: 22px;
  font-weight: 800;
  color: ${brand_orange_deep};
  white-space: nowrap;

  [data-theme="dark"] & {
    color: #fdba74;
  }
`;

const CheckoutMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 18px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;

  [data-theme="dark"] & {
    color: #d1d5db;
  }
`;

const Fieldset = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  border-radius: 12px;
  background: rgba(245, 158, 11, 0.06);
  border: 1px solid rgba(245, 158, 11, 0.28);
  margin-top: 6px;

  [data-theme="dark"] & {
    background: rgba(245, 158, 11, 0.1);
    border-color: rgba(245, 158, 11, 0.34);
  }
`;

const FieldLabel = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #b45309;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #4b5563;
  margin-top: 10px;

  input {
    padding: 10px 12px;
    border-radius: 10px;
    border: 1px solid rgba(15, 23, 42, 0.16);
    font-size: 14px;
    font-weight: 500;
    color: #1f2937;
    background: #fff;
  }

  input:focus-visible {
    outline: 2px solid #fdba74;
    outline-offset: 1px;
  }

  [data-theme="dark"] & {
    color: #cbd5e1;
    input {
      background: rgba(255, 255, 255, 0.05);
      border-color: rgba(255, 255, 255, 0.16);
      color: #f3f4f6;
    }
  }
`;

const CheckoutActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
`;
